import { apiRequest } from './client';
import type {
    DiveCenter,
    DiveCenterAddress,
    DiveCenterClosure,
    DiveCenterCoordinates,
    GooglePlaceDetails,
    OpeningHoursDay,
} from '@/types';

export interface CreateDiveCenterPayload {
    name: string;
    description?: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: DiveCenterAddress;
    coordinates?: DiveCenterCoordinates;
    /**
     * Zona IANA. Mandarla la fija a mano; omitirla deja que el backend la
     * derive de `coordinates`. Mandar `''` fuerza la re-derivación.
     */
    timezone?: string;
    openingHours?: OpeningHoursDay[];
    services?: string[];
    agencies?: string[];
    facilities?: string[];
}

export interface UpdateDiveCenterPayload {
    name?: string;
    description?: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: DiveCenterAddress;
    coordinates?: DiveCenterCoordinates;
    /**
     * Zona IANA. Mandarla la fija a mano; omitirla deja que el backend la
     * derive de `coordinates`. Mandar `''` fuerza la re-derivación.
     */
    timezone?: string;
    openingHours?: OpeningHoursDay[];
    services?: string[];
    agencies?: string[];
    facilities?: string[];
    /** Avisos de "Needs attention" descartados, por clave. */
    dismissedTodos?: string[];
}

export const listDiveCenters = () =>
    apiRequest<{ diveCenters: DiveCenter[] }>('/dive-centers');

export const createDiveCenter = (payload: CreateDiveCenterPayload) =>
    apiRequest<{ diveCenter: DiveCenter }, CreateDiveCenterPayload>('/dive-centers', {
        method: 'POST',
        body: payload,
    });

export const getDiveCenter = (id: string) =>
    apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}`);

export const updateDiveCenter = (id: string, payload: UpdateDiveCenterPayload) =>
    apiRequest<{ diveCenter: DiveCenter }, UpdateDiveCenterPayload>(`/dive-centers/${id}`, {
        method: 'PATCH',
        body: payload,
    });

export const deleteDiveCenter = (id: string) =>
    apiRequest<{ success: boolean; deletedPhotos: number }>(`/dive-centers/${id}`, {
        method: 'DELETE',
    });

export const uploadDiveCenterPhoto = (id: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}/photos`, {
        method: 'POST',
        body: form,
        formData: true,
    });
};

/**
 * Logo del centro (§21.8.3).
 *
 * Endpoint aparte de `/photos`: el logo no entra en el array —consumiría una de
 * las diez fotos y saldría en el carrusel del perfil— y es lo que encabeza el
 * formulario público del QR.
 */
export const uploadDiveCenterLogo = (id: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}/logo`, {
        method: 'POST',
        body: form,
        formData: true,
    });
};

export const deleteDiveCenterLogo = (id: string) =>
    apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}/logo`, {
        method: 'DELETE',
    });

export const deleteDiveCenterPhoto = (id: string, index: number) =>
    apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}/photos/${index}`, {
        method: 'DELETE',
    });

/** Reorder photos (first = cover). `order` is a permutation of current indices. */
export const reorderDiveCenterPhotos = (id: string, order: number[]) =>
    apiRequest<{ diveCenter: DiveCenter }, { order: number[] }>(`/dive-centers/${id}/photos/order`, {
        method: 'PATCH',
        body: { order },
    });

/** Publish a dive center (draft → published). Gated server-side on completeness. */
/**
 * Reemplaza la lista de cierres del centro (§22).
 *
 * Endpoint propio y no el PATCH genérico porque además de guardar rehace el
 * calendario, y la respuesta trae el impacto: cuántas salidas futuras se
 * fueron y cuántas quedaron huérfanas por tener gente anotada.
 */
export interface ClosuresImpact {
    deleted: number;
    detached: number;
    created: number;
    updated: number;
    /** Actividades borradas por el tilde (solo las que no tenían reservas). */
    activitiesDeleted: number;
    /** Las que quedaron porque tienen gente anotada: se resuelven a mano. */
    activitiesKept: number;
}

/** Actividades que siguen en pie dentro de un día cerrado. */
export interface ClosureConflict {
    closureId: string;
    total: number;
    withBookings: number;
}

/**
 * Cierres del centro con sus conflictos.
 *
 * Endpoint aparte del GET del dive center porque los conflictos cuestan una
 * consulta de eventos: quien solo quiere el centro no tiene por qué pagarla.
 */
export const listDiveCenterClosures = (id: string) =>
    apiRequest<{ closures: DiveCenterClosure[]; conflicts: ClosureConflict[] }>(
        `/dive-centers/${id}/closures`,
    );

/**
 * Lo que se manda al guardar. El `id` es opcional porque los cierres nuevos
 * todavía no tienen uno: se lo asigna el backend.
 */
export type ClosurePayload = Omit<DiveCenterClosure, 'id'> & { id?: string };

export interface UpdateClosuresBody {
    closures: ClosurePayload[];
    /** Borrar además las actividades sin reservas que caigan en esos días. */
    deleteActivities?: boolean;
}

export const updateDiveCenterClosures = (
    id: string,
    closures: ClosurePayload[],
    options: { deleteActivities?: boolean } = {},
) =>
    apiRequest<{ diveCenter: DiveCenter; impact: ClosuresImpact }, UpdateClosuresBody>(
        `/dive-centers/${id}/closures`,
        { method: 'PUT', body: { closures, deleteActivities: options.deleteActivities } },
    );

export const publishDiveCenter = (id: string) =>
    apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}/publish`, {
        method: 'PATCH',
    });

/** Unpublish a dive center (→ draft). */
export const unpublishDiveCenter = (id: string) =>
    apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}/unpublish`, {
        method: 'PATCH',
    });

/**
 * Zona horaria IANA de un punto del mapa, resuelta por el backend.
 *
 * La resolución vive allá (mismo `tz-lookup` que corre al guardar) para que lo
 * que el wizard muestra y lo que el centro termina guardando sea el mismo valor.
 * `null` = el punto no resuelve; el campo queda vacío y el guardado la deduce.
 */
export const resolveDiveCenterTimezone = (lat: number, lng: number) =>
    apiRequest<{ timezone: string | null }>(
        `/dive-centers/timezone?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`,
    );

export interface PlaceSuggestion {
    placeId: string;
    name: string;
    address: string | null;
    lat: number;
    lng: number;
    rating: number | null;
    userRatingCount: number | null;
    types: string[];
    /** Present only when the search was run with an origin (DC coordinates). */
    distanceKm?: number;
}

/**
 * Free-text Google Places search. When `lat`/`lng` (the dive center's
 * coordinates) are passed, results are hard-filtered to within `radiusKm`
 * (default 3 km on the backend) and each carries a `distanceKm`.
 */
export const searchProviderPlaces = (
    q: string,
    lat?: number | null,
    lng?: number | null,
    radiusKm?: number,
) => {
    const params = new URLSearchParams({ q });
    if (lat != null && lng != null) {
        params.set('lat', String(lat));
        params.set('lng', String(lng));
        if (radiusKm != null) params.set('radiusKm', String(radiusKm));
    }
    return apiRequest<{ places: PlaceSuggestion[] }>(
        `/dive-centers/google-place/search?${params.toString()}`,
    );
};

export const connectGooglePlace = (id: string, placeId: string) =>
    apiRequest<{ diveCenter: DiveCenter }, { placeId: string }>(
        `/dive-centers/${id}/google-place`,
        { method: 'POST', body: { placeId } },
    );

/** Live Google rating / review count for the connected place (never cached). */
export const getGooglePlaceDetails = (id: string) =>
    apiRequest<GooglePlaceDetails>(`/dive-centers/${id}/google-place/details`);

export const disconnectGooglePlace = (id: string) =>
    apiRequest<{ diveCenter: DiveCenter }>(`/dive-centers/${id}/google-place`, {
        method: 'DELETE',
    });

/**
 * Token del formulario público de alta de clientes (§21). El GET lo crea si el
 * centro todavía no tenía uno.
 */
export const getPublicFormToken = (dcId: string) =>
    apiRequest<{ token: string }>(`/dive-centers/${dcId}/public-form-token`);

/**
 * Rota el token. **Invalida los QR ya impresos** — solo owner/admin, y la UI
 * lo confirma antes.
 */
export const regeneratePublicFormToken = (dcId: string) =>
    apiRequest<{ token: string }>(`/dive-centers/${dcId}/public-form-token`, { method: 'POST' });
